"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html } from "lit";
import { property, query, state } from "lit/decorators.js";
import { STCComponentMixin } from "../../../common/mixins/stc_component_mixin";
import { QuickSearchController } from "./controllers/quick_search_controller";
export class StcQuickSearch extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this.emptyMessage = "";
    this.defaultPlaceholderMessage = "";
    this.canSearchContacts = false;
    this.canSearchHouseholds = false;
    this.canSearchOrganizations = false;
    this.canSearchProspects = false;
    this.quickSearchFilterEnabled = false;
    this.selectedFilter = "all";
    this.displayPlaceholder = "Search";
    this.currentSearchTerm = "";
    this.hasSelectionModifier = false;
    this.quickSearchController = new QuickSearchController(this);
    this.handleSelectionClick = (e) => {
      this.hasSelectionModifier = e.metaKey || e.ctrlKey;
    };
    this.handleSelectionKeyDown = (e) => {
      if (e.key === "Enter") {
        this.hasSelectionModifier = e.metaKey || e.ctrlKey;
      }
    };
  }
  firstUpdated() {
    this.selectedFilter = this.getDefaultFilter();
    this.displayPlaceholder = this.getPlaceholderForFilter(this.selectedFilter);
    this.attachSelectionListeners();
  }
  connectedCallback() {
    super.connectedCallback();
    this.attachSelectionListeners();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.detachSelectionListeners();
  }
  attachSelectionListeners() {
    const searchEl = this.quickSearchElement;
    if (!searchEl) return;
    searchEl.addEventListener("click", this.handleSelectionClick, true);
    searchEl.addEventListener("keydown", this.handleSelectionKeyDown, true);
  }
  detachSelectionListeners() {
    const searchEl = this.quickSearchElement;
    if (!searchEl) return;
    searchEl.removeEventListener("click", this.handleSelectionClick, true);
    searchEl.removeEventListener("keydown", this.handleSelectionKeyDown, true);
  }
  getResults(searchTerm) {
    return this.quickSearchController.fetchResults(
      this.quickSearchUrl,
      searchTerm,
      this.getHeaders(),
      this.selectedFilter
    );
  }
  getFilterOptions() {
    const allEnabled = this.getPermissionCount() > 1;
    return [
      { value: "all", label: "All", enabled: allEnabled },
      { value: "prospect", label: "Prospect", enabled: this.canSearchProspects },
      { value: "contact", label: "Contact", enabled: this.canSearchContacts },
      { value: "household", label: "Household", enabled: this.canSearchHouseholds },
      { value: "organization", label: "Organization", enabled: this.canSearchOrganizations }
    ];
  }
  getPlaceholderForFilter(filter) {
    if (filter === "all") return "Search";
    const filterLabels = {
      "prospect": "prospect",
      "contact": "contact",
      "household": "household",
      "organization": "organization"
    };
    return `Search for ${filterLabels[filter] || filter}`;
  }
  getPermissionCount() {
    return [
      this.canSearchProspects,
      this.canSearchContacts,
      this.canSearchHouseholds,
      this.canSearchOrganizations
    ].filter(Boolean).length;
  }
  getSingleEnabledFilter() {
    const enabledFilters = [
      { value: "prospect", enabled: this.canSearchProspects },
      { value: "contact", enabled: this.canSearchContacts },
      { value: "household", enabled: this.canSearchHouseholds },
      { value: "organization", enabled: this.canSearchOrganizations }
    ].filter((opt) => opt.enabled);
    return enabledFilters.length === 1 ? enabledFilters[0].value : null;
  }
  getDefaultFilter() {
    const singleFilter = this.getSingleEnabledFilter();
    return singleFilter || "all";
  }
  isQuickSearchFilterEnabled() {
    return this.quickSearchFilterEnabled;
  }
  handleFilterChange(e) {
    this.selectedFilter = e.detail.value;
    this.displayPlaceholder = this.getPlaceholderForFilter(this.selectedFilter);
    if (this.currentSearchTerm && this.currentSearchTerm.length > 0) {
      this.getResults(this.currentSearchTerm).then((results) => {
        if (results === void 0) return;
        this.searchResults = results;
      });
    }
  }
  getHeaders() {
    return {
      "X-CSRF-Token": this.formAuthenticityToken,
      "X-Requested-With": "XMLHttpRequest"
    };
  }
  clearQuickSearch() {
    this.getResults("");
    this.currentSearchTerm = "";
    this.searchResults = [];
    const searchEl = this.quickSearchElement;
    searchEl?.clearInput?.();
  }
  handleCommitEnded(e) {
    const href = e.detail.value.value;
    const openInNewTab = this.hasSelectionModifier;
    this.hasSelectionModifier = false;
    if (openInNewTab) {
      window.open(href, "_blank");
      this.clearQuickSearch();
    } else {
      const currentUrl = new URL(window.location.href);
      const newUrl = new URL(href, window.location.origin);
      if (currentUrl.pathname === newUrl.pathname && currentUrl.hash !== newUrl.hash) {
        window.location.href = href;
        window.location.reload();
      } else {
        location.assign(href);
      }
    }
  }
  handleSearchValueChanged(e) {
    this.currentSearchTerm = e.detail.value;
    this.getResults(this.currentSearchTerm).then((results) => {
      if (results === void 0) return;
      this.searchResults = results;
    });
  }
  shouldHideStatusMessage() {
    if (this.searchResults === void 0) return true;
    return this.searchResults.length > 0;
  }
  render() {
    const filterOptions = this.getFilterOptions();
    const quickSearchFilterEnabled = this.isQuickSearchFilterEnabled();
    const permissionCount = this.getPermissionCount();
    const showDropdown = quickSearchFilterEnabled && permissionCount >= 2;
    return html`
      <div class="quick-search-container ${showDropdown ? "has-dropdown" : ""}" style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem; width: 100%;">
        ${showDropdown ? html`
          <hi-dropdown
            id="quick-search-filter-dropdown"
            aesthetic="tight"
            cap-height
          >
            <hi-select
              id="quick-search-filter"
              aesthetic="dropdown"
              mode="value"
              value=${this.selectedFilter}
              @value-changed=${this.handleFilterChange}
            >
              ${filterOptions.filter((option) => option.enabled).map((option) => html`
                  <hi-option
                    value=${option.value}
                    ?selected=${option.value === this.selectedFilter}
                  >
                    ${option.label}
                  </hi-option>
                `)}
            </hi-select>
          </hi-dropdown>
        ` : ""}

        <hi-search
          id="top-nav__quick-search"
          kind="dynamic"
          hidden-result-message
          aria-label="quick search"
          class="top-nav__quick-search"
          style="flex: 1 1 auto; min-width: 0;"
          placeholder=${this.displayPlaceholder}
          ?hidden-status-message=${this.shouldHideStatusMessage()}
          .input=${this.searchResults}
          @commit-ended=${this.handleCommitEnded}
          @search-value-changed=${this.handleSearchValueChanged}
        >
          <p slot="empty-message" class="text-light">
            ${this.emptyMessage}
          </p>
        </hi-search>
      </div>
    `;
  }
}
__decorateClass([
  property({ type: Boolean })
], StcQuickSearch.prototype, "ignoreGroups", 2);
__decorateClass([
  property({ type: String })
], StcQuickSearch.prototype, "quickSearchUrl", 2);
__decorateClass([
  property({ type: String })
], StcQuickSearch.prototype, "formAuthenticityToken", 2);
__decorateClass([
  property({ type: Array })
], StcQuickSearch.prototype, "searchResults", 2);
__decorateClass([
  property({ type: String, attribute: "empty-message" })
], StcQuickSearch.prototype, "emptyMessage", 2);
__decorateClass([
  property({ type: String, attribute: "placeholder-message" })
], StcQuickSearch.prototype, "defaultPlaceholderMessage", 2);
__decorateClass([
  property({ type: Boolean, attribute: "can-search-contacts" })
], StcQuickSearch.prototype, "canSearchContacts", 2);
__decorateClass([
  property({ type: Boolean, attribute: "can-search-households" })
], StcQuickSearch.prototype, "canSearchHouseholds", 2);
__decorateClass([
  property({ type: Boolean, attribute: "can-search-organizations" })
], StcQuickSearch.prototype, "canSearchOrganizations", 2);
__decorateClass([
  property({ type: Boolean, attribute: "can-search-prospects" })
], StcQuickSearch.prototype, "canSearchProspects", 2);
__decorateClass([
  property({ type: Boolean, attribute: "quick-search-filter-enabled" })
], StcQuickSearch.prototype, "quickSearchFilterEnabled", 2);
__decorateClass([
  state()
], StcQuickSearch.prototype, "selectedFilter", 2);
__decorateClass([
  state()
], StcQuickSearch.prototype, "displayPlaceholder", 2);
__decorateClass([
  state()
], StcQuickSearch.prototype, "currentSearchTerm", 2);
__decorateClass([
  state()
], StcQuickSearch.prototype, "abortController", 2);
__decorateClass([
  query("#top-nav__quick-search")
], StcQuickSearch.prototype, "quickSearchElement", 2);
customElements.define("stc-quick-search", StcQuickSearch);
